'use client';

import React from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'href'> {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'giant' | 'large' | 'medium' | 'small' | 'tiny';
  disabled?: boolean;
  iconOnly?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  /**
   * Si défini (et non `disabled`), rend une balise `<a>` avec les mêmes styles qu’un bouton
   * (navigation native : clic, nouvel onglet, copie du lien).
   */
  href?: string;
  /** Cible du lien — défaut `_blank` pour les URLs externes typiques. */
  linkTarget?: React.HTMLAttributeAnchorTarget;
  /** Surcharge du `rel` (sinon `noopener noreferrer` quand `linkTarget` vaut `_blank`). */
  linkRel?: string;
}

export const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'medium',
      disabled = false,
      iconOnly = false,
      leftIcon,
      rightIcon,
      children = 'Button',
      className = '',
      href,
      linkTarget = '_blank',
      linkRel,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const baseClasses = 'button-base';
    const variantClasses = `button-${variant}`;
    const sizeClasses = `button-${size}`;
    const iconOnlyClasses = iconOnly ? 'button-icon-only' : '';
    const classNames =
      `${baseClasses} ${variantClasses} ${sizeClasses} ${iconOnlyClasses} ${className}`.trim();

    const inner = (
      <>
        {leftIcon && <span className="button-icon">{leftIcon}</span>}
        {!iconOnly && children}
        {rightIcon && <span className="button-icon">{rightIcon}</span>}
      </>
    );

    if (href && !disabled) {
      const rel =
        linkRel ?? (linkTarget === '_blank' ? 'noopener noreferrer' : undefined);
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={href}
          target={linkTarget}
          rel={rel}
          className={classNames}
          {...(props as Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'children' | 'className'>)}
        >
          {inner}
        </a>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type}
        className={classNames}
        disabled={disabled}
        {...props}
      >
        {inner}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
